import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Bot, Send, ShieldAlert, CheckCircle2, XCircle, MinusCircle, History, Loader2 } from "lucide-react";

const EXAMPLES = [
  "Aggiungi Villanova Canavese.",
  "Aggiungi 3 pizzerie: Pizzeria X — via Roma 1, 011 111, Lun-Dom 19-23; Pizzeria Y — via Torino 5, 011 222; Pizzeria Z — corso Italia 9, 011 333.",
  "Cambia gli orari della Pizzeria X a Lun-Dom 19:00-23:00.",
  "Crea una missione di lancio per Villanova Canavese.",
];

const STATUS_ICON = { done: CheckCircle2, partial: MinusCircle, skipped: MinusCircle, error: XCircle };
const STATUS_COLOR = { done: "#00E676", partial: "#FFD700", skipped: "#8AA", error: "#F87171" };

function ResultRow({ r }) {
  const Icon = STATUS_ICON[r.status] || CheckCircle2;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={`ai-result-${r.tool}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: STATUS_COLOR[r.status] || "#8AA" }} />
      <div><span className="font-mono text-xs text-zinc-500">{r.tool}</span> · {r.message}</div>
    </div>
  );
}

export default function AiOperatorPanel() {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);   // plan awaiting confirmation
  const [convo, setConvo] = useState([]);          // {role, ...}
  const [activity, setActivity] = useState([]);
  const endRef = useRef(null);

  const loadActivity = () => api.get("/ceo/ai/activity").then(({ data }) => setActivity(data.actions || [])).catch(() => {});
  useEffect(() => { loadActivity(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo, pending]);

  const send = async (text) => {
    const msg = (text ?? instruction).trim();
    if (!msg || loading) return;
    setInstruction(""); setPending(null);
    setConvo((c) => [...c, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ceo/ai/agent", { instruction: msg });
      if (data.requires_confirmation) {
        setPending({ ...data, instruction: msg });
      } else {
        setConvo((c) => [...c, { role: "assistant", summary: data.summary, results: data.results }]);
        loadActivity();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ceo/ai/confirm", { confirm_token: pending.confirm_token });
      setConvo((c) => [...c, { role: "assistant", summary: pending.summary, results: data.results }]);
      setPending(null); loadActivity();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="ai-operator-panel">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Bot className="h-5 w-5 text-[#00E676]" /> Assistente Operativo</h2>
          <p className="text-sm text-zinc-500">Impartisci istruzioni in linguaggio naturale: l'AI esegue operazioni reali sulla piattaforma (RBAC, audit, conferma per azioni sensibili).</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex, i) => (
            <button key={i} onClick={() => send(ex)} data-testid={`ai-example-${i}`}
              className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-[#1C1C1F] text-zinc-400 hover:border-[#00E676]/40">{ex.length > 42 ? ex.slice(0, 42) + "…" : ex}</button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0F1016] p-4 min-h-[240px] space-y-4">
          {convo.length === 0 && !pending && <div className="text-zinc-600 text-sm">Scrivi un'istruzione per iniziare.</div>}
          {convo.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="text-right"><span className="inline-block bg-[#00E676]/15 border border-[#00E676]/30 rounded-2xl px-3 py-2 text-sm">{m.text}</span></div>
            ) : (
              <div key={i} className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-3 space-y-2" data-testid="ai-assistant-msg">
                {m.summary && <div className="text-sm text-zinc-300">{m.summary}</div>}
                {(m.results || []).map((r, j) => <ResultRow key={j} r={r} />)}
              </div>
            )
          ))}

          {pending && (
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-3" data-testid="ai-confirm-box">
              <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm"><ShieldAlert className="h-4 w-4" /> {pending.message}</div>
              {pending.summary && <div className="text-sm text-zinc-300">{pending.summary}</div>}
              <ul className="space-y-1 text-sm">
                {pending.plan.map((p, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-zinc-500">{p.tool}</span>
                    {p.sensitive && <span className="text-[10px] uppercase text-amber-400 border border-amber-500/40 rounded px-1">sensibile</span>}
                    <span className="text-zinc-400">{p.reason}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button onClick={confirm} data-testid="ai-confirm-btn" disabled={loading} className="px-4 py-2 rounded-full bg-[#00E676] text-black font-semibold text-sm disabled:opacity-40">Conferma ed esegui</button>
                <button onClick={() => setPending(null)} data-testid="ai-cancel-btn" className="px-4 py-2 rounded-full border border-white/15 text-sm">Annulla</button>
              </div>
            </div>
          )}
          {loading && <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Sto lavorando…</div>}
          <div ref={endRef} />
        </div>

        <div className="flex gap-2">
          <input data-testid="ai-instruction-input" value={instruction} onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Es. Aggiungi Villanova Canavese e crea una missione di lancio."
            className="flex-1 bg-[#1C1C1F] border border-white/10 rounded-full px-4 py-3 text-white text-sm" />
          <button data-testid="ai-send-btn" onClick={() => send()} disabled={loading}
            className="px-5 rounded-full bg-[#00E676] text-black font-semibold disabled:opacity-40 flex items-center gap-1.5"><Send className="h-4 w-4" /></button>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2"><History className="h-3.5 w-3.5" /> AI Activity Log</div>
        {activity.length === 0 ? (
          <div className="text-zinc-600 text-sm p-4 rounded-xl bg-[#1C1C1F] border border-white/10" data-testid="ai-activity-empty">Nessuna azione registrata.</div>
        ) : (
          <ul className="space-y-2 max-h-[520px] overflow-auto" data-testid="ai-activity-log">
            {activity.map((a, i) => {
              const Icon = STATUS_ICON[a.status] || CheckCircle2;
              return (
                <li key={i} className="rounded-xl bg-[#1C1C1F] border border-white/10 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs" style={{ color: STATUS_COLOR[a.status] || "#8AA" }}>{a.tool}</span>
                    <span className="text-[10px] text-zinc-500">{new Date(a.at).toLocaleString("it-IT")}</span>
                  </div>
                  <div className="text-zinc-400 mt-1 flex items-start gap-1.5"><Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: STATUS_COLOR[a.status] || "#8AA" }} />{a.result?.message || a.status}</div>
                  <div className="text-[10px] text-zinc-600 mt-1 truncate">« {a.instruction} » · {a.by}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
